// Manage My Booking pages: notes toggles, emailing documents, the pay dialog, the toast,
// and closing the (site) mobile menu when the window grows past phone width.
import { bindDialog } from '@/utils/dialog';

const page = document.querySelector('[data-mmb]');
if (page) {
    initNotes(page);
    initDocumentEmails(page);
    initPayDialog();
    initDepositForm();
    initToast();
    initMenuReset();
}

function initNotes(root) {
    root.querySelectorAll('[data-mmb-notes-toggle]').forEach((button) => {
        button.addEventListener('click', () => {
            const panel = document.getElementById(button.getAttribute('aria-controls'));
            const open = button.getAttribute('aria-expanded') !== 'true';
            panel.hidden = !open;
            button.setAttribute('aria-expanded', String(open));
            button.textContent = open ? button.dataset.hide : button.dataset.show;
        });
    });
}

function initDocumentEmails(root) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content ?? '';

    root.querySelectorAll('[data-mmb-email]').forEach((button) => {
        button.addEventListener('click', async () => {
            button.disabled = true;
            button.setAttribute('aria-busy', 'true');
            try {
                const response = await fetch(button.dataset.mmbEmail, {
                    method: 'POST',
                    headers: { Accept: 'application/json', 'X-CSRF-TOKEN': token, 'X-Requested-With': 'XMLHttpRequest' },
                    credentials: 'same-origin',
                });
                if (response.status === 401) {
                    window.location.reload();
                    return;
                }
                const data = await response.json().catch(() => ({}));
                if (response.status === 429) {
                    showToast('You have emailed this document several times. Please try again later or download it.', 'error');
                } else if (!response.ok) {
                    // Only our own 422 carries a message meant for the customer
                    showToast(response.status === 422 && data.message ? data.message : "We couldn't email the document just now. Please try again or download it.", 'error');
                } else {
                    showToast(data.message, 'success');
                }
            } catch {
                showToast("We couldn't email the document just now. Please try again or download it.", 'error');
            } finally {
                button.disabled = false;
                button.removeAttribute('aria-busy');
            }
        });
    });
}

function initPayDialog() {
    const dialog = document.querySelector('[data-mmb-pay]');
    const openButton = document.querySelector('[data-mmb-pay-open]');
    if (!dialog || !openButton) {
        return;
    }

    const form = dialog.querySelector('[data-mmb-pay-form]');
    const part = dialog.querySelector('[data-mmb-part]');
    const amount = form.elements.amount;
    const amountLabel = dialog.querySelector('[data-mmb-pay-amount]');
    const full = form.querySelector('input[name="option"][value="full"]');
    const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
    const balance = Number(full.dataset.amount);

    const isPart = () => form.elements.option.value === 'part';
    const partAmount = () => Number(amount.value.replace(/[£,\s]/g, ''));
    const refresh = () => {
        part.hidden = !isPart();
        const value = isPart() ? partAmount() : balance;
        amountLabel.textContent = Number.isFinite(value) && value > 0 ? money.format(value) : '£0.00';
    };

    bindDialog(dialog, () => openButton.focus());
    openButton.addEventListener('click', () => {
        refresh();
        dialog.showModal();
    });
    const error = part.querySelector('.mmb-field__error');
    // Shows the amount error; returns false when the amount can't be paid
    const validate = () => {
        const value = partAmount();
        let message = '';
        if (!Number.isFinite(value) || value <= 0) {
            message = 'Please enter the amount you would like to pay.';
        } else if (value < 1) {
            message = 'The smallest amount you can pay is £1.00.';
        } else if (value > balance) {
            message = `The most you can pay is your balance of ${money.format(balance)}.`;
        }
        part.classList.toggle('mmb-field--error', message !== '');
        amount.setAttribute('aria-invalid', message ? 'true' : 'false');
        error.textContent = message;
        error.hidden = message === '';

        return message === '';
    };

    form.addEventListener('change', refresh);
    amount.addEventListener('input', () => {
        refresh();
        if (!error.hidden) {
            validate();
        }
    });

    form.addEventListener('submit', (event) => {
        if (isPart() && !validate()) {
            event.preventDefault();
            amount.focus();
            return;
        }
        form.querySelector('[type="submit"]').disabled = true;
    });

    // Server-side validation failed: show the dialog again with the error
    if (dialog.hasAttribute('data-open')) {
        refresh();
        dialog.showModal();
        amount.focus();
    }
}

// The deposit button posts straight on to the payment provider: one submit only
function initDepositForm() {
    const form = document.querySelector('[data-mmb-deposit-form]');
    form?.addEventListener('submit', () => {
        form.querySelector('[type="submit"]').disabled = true;
    });
}

let toastTimer = null;

function showToast(message, type = 'success') {
    const toast = document.querySelector('[data-mmb-toast]');
    if (!toast) {
        return;
    }
    toast.querySelector('[data-mmb-toast-text]').textContent = message;
    toast.classList.toggle('mmb-toast--error', type === 'error');
    toast.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
        toast.hidden = true;
    }, type === 'error' ? 6000 : 4000);
}

function initToast() {
    const toast = document.querySelector('[data-mmb-toast]');
    const type = toast?.dataset.initialType;
    if (type) {
        showToast(toast.querySelector('[data-mmb-toast-text]').textContent, type);
    }
}

// shell.js only closes its menu above 1000px; here the menu is for phones only.
function initMenuReset() {
    const phone = window.matchMedia('(max-width: 600px)');
    const shell = document.querySelector('.mmb-header[data-shell]');
    const button = shell?.querySelector('[data-menu-toggle]');
    if (!button) {
        return;
    }
    phone.addEventListener('change', () => {
        if (!phone.matches && button.getAttribute('aria-expanded') === 'true') {
            button.click();
        }
    });
}
